/**
 * Which language the speech engine should read a document in.
 *
 * The OCR prompt preserves the source language and expects Polish, English and
 * German (app/api/ocr/route.ts), but the speech side used to hard-code `pl-PL`
 * on every utterance. An English page was therefore read by a Polish voice with
 * Polish phonetics, and - worse - the Polish number/date/abbreviation expansion
 * in cleanTextForTTS ran over it too, turning "min." into "minut".
 *
 * Detection here is deliberately a small heuristic rather than a dependency: the
 * three languages are far apart in both diacritics and function words, the input
 * is a whole document rather than a phrase, and the reader can always override
 * the result from the speech bar.
 *
 * DORMANT, NOT DEAD (DYS-044). The client asked for Polish only, so the reading
 * language is now fixed to 'pl' where the speech manager is constructed, and no
 * UI calls detectSpeechLanguage or offers SPEECH_LANGUAGES any more. Everything
 * below - the detector, the English and German tables, the tag matching - is kept
 * whole and tested on purpose: re-enabling the other languages should be putting
 * a setting back behind that one call, not writing this again. Do not delete the
 * en/de entries as unused code.
 */

export type SpeechLanguage = 'pl' | 'en' | 'de'

/** The setting value meaning "use whatever was detected". */
export const SPEECH_LANGUAGE_AUTO = 'auto'

/** What a language setting can hold: a fixed language or automatic detection. */
export type SpeechLanguageSetting = SpeechLanguage | typeof SPEECH_LANGUAGE_AUTO

/**
 * Used when detection has too little to go on. Polish, because that is what
 * nearly every document in this app is, and because it is the language the
 * Polish-specific TTS text rules in text-util.ts were written for.
 */
export const DEFAULT_SPEECH_LANGUAGE: SpeechLanguage = 'pl'

/** BCP-47 tags handed to SpeechSynthesisUtterance.lang. */
const LANGUAGE_TAGS: Record<SpeechLanguage, string> = {
  pl: 'pl-PL',
  en: 'en-US',
  de: 'de-DE',
}

/** Labels for the speech bar, each written in its own language. */
const LANGUAGE_LABELS: Record<SpeechLanguage, string> = {
  pl: 'Polski',
  en: 'English',
  de: 'Deutsch',
}

export const SPEECH_LANGUAGES: SpeechLanguage[] = ['pl', 'en', 'de']

export function speechLanguageTag(language: SpeechLanguage): string {
  return LANGUAGE_TAGS[language] ?? LANGUAGE_TAGS[DEFAULT_SPEECH_LANGUAGE]
}

export function speechLanguageLabel(language: SpeechLanguage): string {
  return LANGUAGE_LABELS[language] ?? LANGUAGE_LABELS[DEFAULT_SPEECH_LANGUAGE]
}

export function isSpeechLanguage(value: unknown): value is SpeechLanguage {
  return typeof value === 'string' && (SPEECH_LANGUAGES as string[]).includes(value)
}

/**
 * Every tag prefix a voice for this language might report.
 *
 * The two-letter code is the one the spec asks for, but engines do not all comply:
 * Android's text-to-speech commonly reports the three-letter ISO 639-2 code, so a
 * perfectly good Polish voice arrives as "pol" or "pol-POL" and a prefix test
 * against "pl" throws it away. German has two of those codes, "deu" and "ger".
 */
const LANGUAGE_TAG_PREFIXES: Record<SpeechLanguage, string[]> = {
  pl: ['pl', 'pol'],
  en: ['en', 'eng'],
  de: ['de', 'deu', 'ger'],
}

/**
 * What a voice for this language tends to be called, in English and in Polish.
 *
 * Used only when the tag names no language we know - blank, "und", missing
 * entirely - which some engines do while still naming the voice correctly. The
 * specific first names are the ones the platforms ship for Polish; the original
 * matcher listed a couple of them for the same reason.
 */
const LANGUAGE_NAME_HINTS: Record<SpeechLanguage, RegExp> = {
  pl: /polish|polski|polska|zosia|paulina|zofia|marek|agnieszka|jacek|krzysztof/i,
  en: /english|angielski|british|american/i,
  de: /german|deutsch|niemiecki/i,
}

/** Just the parts of a voice this module needs, so it is testable without one. */
export interface VoiceDescriptor {
  lang: string
  name: string
}

function normalizeTag(lang: string): string {
  return (lang || '').toLowerCase().replace(/_/g, '-')
}

function tagNamesLanguage(tag: string, language: SpeechLanguage): boolean {
  return LANGUAGE_TAG_PREFIXES[language].some((prefix) => tag.startsWith(prefix))
}

/**
 * Whether this voice speaks the given language.
 *
 * Shared by the automatic ranking and by the picker on purpose: if the two
 * disagreed, the list would offer a voice the ranking refuses to consider, or
 * hide one it would have chosen.
 *
 * The name is only consulted when the tag names none of the languages we support.
 * A tag that clearly says another one is trusted over any hint in the name, so an
 * English voice cannot be offered as Polish because of a word in its title.
 */
export function voiceMatchesLanguage(voice: VoiceDescriptor, language: SpeechLanguage): boolean {
  const tag = normalizeTag(voice.lang)

  if (tagNamesLanguage(tag, language)) {
    return true
  }

  const tagClaimsAnotherLanguage = SPEECH_LANGUAGES.some(
    (other) => other !== language && tagNamesLanguage(tag, other)
  )
  if (tagClaimsAnotherLanguage) {
    return false
  }

  return LANGUAGE_NAME_HINTS[language].test(voice.name || '')
}

/** Whether the voice's own tag is the exact regional tag we would have asked for. */
export function voiceTagIsExact(voice: VoiceDescriptor, language: SpeechLanguage): boolean {
  return normalizeTag(voice.lang) === speechLanguageTag(language).toLowerCase()
}

/**
 * Letters that only one of the three languages uses.
 *
 * These are the strongest signal available and cheap to count. 'ó' is left out
 * of the Polish set on purpose - it also occurs in other scripts this heuristic
 * may meet - and the remaining eight are effectively Polish-only. 'ß' and the
 * umlauts are the German equivalent.
 */
const POLISH_ONLY_LETTERS = /[ąćęłńśźż]/g
const GERMAN_ONLY_LETTERS = /[äöüß]/g

/**
 * Function words per language, chosen to be discriminative.
 *
 * Words shared between two of these languages are omitted rather than counted
 * for both: Polish "to"/"w"/"i"/"o" collide with English, German "in"/"man" with
 * English, and counting them on both sides only adds noise. What is left is a
 * word that, when seen, argues for exactly one language.
 */
const FUNCTION_WORDS: Record<SpeechLanguage, string[]> = {
  pl: [
    'nie', 'się', 'jest', 'że', 'oraz', 'jako', 'przez', 'który', 'która', 'które',
    'tego', 'tym', 'tylko', 'już', 'można', 'będzie', 'jeśli', 'także', 'bardzo',
    'wszystkie', 'na', 'do', 'jak', 'ale', 'dla', 'po', 'są', 'lub', 'sobie',
    'czy', 'gdy', 'aby', 'przy', 'bez', 'pod', 'nad', 'ich', 'jego', 'jej',
    'więc', 'może', 'byli', 'była', 'było', 'ponieważ', 'każdy', 'nasze',
  ],
  en: [
    'the', 'and', 'of', 'is', 'that', 'for', 'with', 'as', 'are', 'be', 'this',
    'have', 'from', 'not', 'but', 'they', 'you', 'his', 'her', 'which', 'their',
    'was', 'were', 'it', 'at', 'by', 'an', 'or', 'we', 'can', 'has', 'will',
    'would', 'there', 'what', 'all', 'been', 'more', 'about', 'when', 'them',
  ],
  de: [
    'der', 'die', 'das', 'und', 'ist', 'nicht', 'den', 'dem', 'ein', 'eine',
    'einen', 'zu', 'mit', 'sich', 'auf', 'für', 'von', 'im', 'dass', 'auch',
    'werden', 'wird', 'sind', 'aber', 'oder', 'als', 'aus', 'bei', 'nach',
    'wenn', 'nur', 'noch', 'wie', 'haben', 'hat', 'sie', 'es', 'dies', 'durch',
    'diese', 'einem', 'einer', 'schon', 'sehr', 'kann',
  ],
}

const FUNCTION_WORD_SETS: Record<SpeechLanguage, Set<string>> = {
  pl: new Set(FUNCTION_WORDS.pl),
  en: new Set(FUNCTION_WORDS.en),
  de: new Set(FUNCTION_WORDS.de),
}

/**
 * A language-only letter is worth more than a function word hit.
 *
 * One 'ł' settles Polish against English on its own, whereas a single "na" or
 * "die" can appear inside a foreign name or a quotation. Three is enough to let
 * diacritics dominate without making a stray character decisive - the floor
 * below still demands several signals in total.
 */
const LETTER_WEIGHT = 3

/**
 * Minimum total score before the result is trusted.
 *
 * Below this the text is a fragment, a page number, or a heading, and the
 * default is a better answer than the winner of a two-to-one vote.
 */
const MINIMUM_SCORE = 4

/** How much the winner must beat the runner-up by to be believed. */
const MINIMUM_MARGIN = 2

function countMatches(text: string, pattern: RegExp): number {
  const matches = text.match(pattern)
  return matches ? matches.length : 0
}

export interface SpeechLanguageDetection {
  language: SpeechLanguage
  /** False when the text was too short or too mixed to call, and the default was used. */
  confident: boolean
  scores: Record<SpeechLanguage, number>
}

/**
 * Scores the text for each supported language and returns the winner.
 *
 * Only the first few thousand characters are examined. A document's language
 * does not change halfway through often enough to justify walking seventeen
 * pages of text on every playback, and the signal saturates long before that.
 */
export function detectSpeechLanguageDetailed(text: string): SpeechLanguageDetection {
  const scores: Record<SpeechLanguage, number> = { pl: 0, en: 0, de: 0 }

  const sample = (text || '').slice(0, 4000).toLowerCase()

  if (!sample.trim()) {
    return { language: DEFAULT_SPEECH_LANGUAGE, confident: false, scores }
  }

  scores.pl += LETTER_WEIGHT * countMatches(sample, POLISH_ONLY_LETTERS)
  scores.de += LETTER_WEIGHT * countMatches(sample, GERMAN_ONLY_LETTERS)

  // Words are compared without their punctuation so that "nicht," and "(the"
  // still count. Apostrophes stay, because "don't" is one English word.
  const words = sample.match(/[a-zà-ɏ'']+/g) || []
  for (const word of words) {
    const cleaned = word.replace(/^['']+|['']+$/g, '')
    if (!cleaned) continue
    for (const language of SPEECH_LANGUAGES) {
      if (FUNCTION_WORD_SETS[language].has(cleaned)) {
        scores[language] += 1
      }
    }
  }

  const ranked = SPEECH_LANGUAGES
    .map((language) => ({ language, score: scores[language] }))
    .sort((a, b) => b.score - a.score)

  const [winner, runnerUp] = ranked

  const confident =
    winner.score >= MINIMUM_SCORE &&
    winner.score - (runnerUp?.score ?? 0) >= MINIMUM_MARGIN

  return {
    language: confident ? winner.language : DEFAULT_SPEECH_LANGUAGE,
    confident,
    scores,
  }
}

/** Convenience wrapper for callers that only need the language. */
export function detectSpeechLanguage(text: string): SpeechLanguage {
  return detectSpeechLanguageDetailed(text).language
}

/**
 * Resolves a stored setting against the text in front of the reader.
 * An explicit choice always wins - that is the point of the override.
 */
export function resolveSpeechLanguage(
  setting: SpeechLanguageSetting,
  text: string
): SpeechLanguage {
  if (isSpeechLanguage(setting)) {
    return setting
  }
  return detectSpeechLanguage(text)
}
